import React, { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';
import { Plus, MessageSquare, Clock, CheckCircle, ChevronRight, ArrowLeft, Send } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

export default function SupportTicketsPage({ handleNavigation }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'create', 'detail'
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Create Form State
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('Yazılım');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reply State
  const [replyMessage, setReplyMessage] = useState('');

  const loadTickets = async () => {
    setLoading(true);
    try {
      const data = await apiClient('TenantSupport');
      setTickets(data);
    } catch (err) {
      setError(err.message || 'Biletler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'list') {
      loadTickets();
    }
  }, [viewMode]);

  const loadTicketDetail = async (id) => {
    setLoading(true);
    try {
      const data = await apiClient(`TenantSupport/${id}`);
      setSelectedTicket(data);
      setViewMode('detail');
    } catch (err) {
      setError(err.message || 'Bilet detayı yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await apiClient('TenantSupport', {
        method: 'POST',
        body: JSON.stringify({ subject, category, message })
      });
      setSubject('');
      setMessage('');
      setViewMode('list');
    } catch (err) {
      alert('Bilet oluşturulamadı: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;
    setIsSubmitting(true);
    try {
      await apiClient(`TenantSupport/${selectedTicket.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message: replyMessage })
      });
      setReplyMessage('');
      await loadTicketDetail(selectedTicket.id); // Yenile
    } catch (err) {
      alert('Cevap gönderilemedi: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Açık': return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800"><Clock className="w-3 h-3 inline mr-1"/> Açık</span>;
      case 'Cevaplandı': return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800"><MessageSquare className="w-3 h-3 inline mr-1"/> Cevaplandı</span>;
      case 'Çözüldü': return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800"><CheckCircle className="w-3 h-3 inline mr-1"/> Çözüldü</span>;
      default: return <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700">{status}</span>;
    }
  };

  if (error && error.includes('aktif değildir')) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center">
        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
          <MessageSquare className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Destek Sistemi Aktif Değil</h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">Firmanız için destek sistemi özelliği kapalıdır. Lütfen sistem yöneticinizle iletişime geçin.</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-h-[70vh] flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center gap-4">
          {viewMode !== 'list' && (
            <button
              onClick={() => setViewMode('list')}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              {viewMode === 'list' ? 'Destek Taleplerim' : viewMode === 'create' ? 'Yeni Talep Oluştur' : 'Talep Detayı'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">SIRIUS destek ekibiyle iletişime geçin.</p>
          </div>
        </div>
        {viewMode === 'list' && (
          <button
            onClick={() => setViewMode('create')}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition transform hover:scale-105"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Talep
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : viewMode === 'list' ? (
          /* LİSTE GÖRÜNÜMÜ */
          tickets.length === 0 ? (
            <div className="text-center py-20">
              <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Henüz talep oluşturmadınız</h3>
              <p className="mt-1 text-gray-500 dark:text-gray-400">Bir sorununuz mu var? Yeni talep oluşturarak bize ulaşın.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map(ticket => (
                <div 
                  key={ticket.id}
                  onClick={() => loadTicketDetail(ticket.id)}
                  className={`relative bg-white dark:bg-gray-800 border rounded-lg p-5 cursor-pointer hover:shadow-md transition-all group overflow-hidden ${
                    ticket.hasUnreadMessage 
                      ? 'border-blue-300 dark:border-blue-700' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                  }`}
                >
                  {ticket.hasUnreadMessage && (
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500 animate-pulse"></div>
                  )}
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatusBadge(ticket.status)}
                        {ticket.hasUnreadMessage && (
                          <span className="text-xs font-bold text-red-500 animate-pulse flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                            Yeni Mesaj
                          </span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                          {ticket.category}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {format(new Date(ticket.updatedAt), 'dd MMM HH:mm', { locale: tr })}
                        </span>
                      </div>
                      <h3 className={`text-lg font-semibold group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors ${ticket.hasUnreadMessage ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                        {ticket.subject}
                      </h3>
                    </div>
                    <ChevronRight className={`w-5 h-5 transition-colors mt-2 ${ticket.hasUnreadMessage ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-500'}`} />
                  </div>
                </div>
              ))}
            </div>
          )
        ) : viewMode === 'create' ? (
          /* OLUŞTURMA GÖRÜNÜMÜ */
          <form onSubmit={handleCreateTicket} className="max-w-2xl mx-auto space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kategori</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="Yazılım">Yazılım & Uygulama Hatası</option>
                <option value="Donanım">Cihaz & Donanım</option>
                <option value="Muhasebe">Fatura & Ödeme</option>
                <option value="Diğer">Diğer Konular</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Konu Özeti</label>
              <input
                type="text"
                required
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Örn: Cihaz bağlantısı koptu"
                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mesajınız</label>
              <textarea
                required
                rows="6"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Yaşadığınız sorunu detaylıca anlatınız..."
                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              ></textarea>
            </div>
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg shadow-md transition"
              >
                {isSubmitting ? 'Gönderiliyor...' : 'Talebi Gönder'}
                <Send className="w-4 h-4 ml-2" />
              </button>
            </div>
          </form>
        ) : selectedTicket && (
          /* DETAY & MESAJLAŞMA GÖRÜNÜMÜ */
          <div className="flex flex-col h-full">
            {/* Ticket Info Card */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{selectedTicket.subject}</h2>
              <div className="flex items-center gap-4 text-sm">
                {getStatusBadge(selectedTicket.status)}
                <span className="text-gray-500 dark:text-gray-400">Kategori: {selectedTicket.category}</span>
                <span className="text-gray-500 dark:text-gray-400">Tarih: {format(new Date(selectedTicket.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })}</span>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto space-y-6 mb-6 px-2">
              {selectedTicket.messages?.map((msg, idx) => {
                const isMine = !msg.isFromAdmin;
                return (
                  <div key={idx} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl p-4 shadow-sm ${
                      isMine 
                        ? 'bg-blue-600 text-white rounded-br-none' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-none border border-gray-200 dark:border-gray-600'
                    }`}>
                      <div className="text-[15px] whitespace-pre-wrap leading-relaxed">{msg.message}</div>
                      <div className={`text-[11px] mt-2 text-right ${isMine ? 'text-blue-200' : 'text-gray-400'}`}>
                        {format(new Date(msg.createdAt), 'HH:mm - dd MMM', { locale: tr })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reply Box */}
            {selectedTicket.status !== 'Çözüldü' && (
              <form onSubmit={handleReply} className="mt-auto bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2 flex items-end gap-2">
                <textarea
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  placeholder="Cevabınızı yazın..."
                  className="flex-1 bg-transparent border-none focus:ring-0 resize-none p-3 text-gray-900 dark:text-white max-h-32"
                  rows={replyMessage.split('\n').length > 1 ? Math.min(replyMessage.split('\n').length, 4) : 1}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleReply(e);
                    }
                  }}
                />
                <button
                  type="submit"
                  disabled={!replyMessage.trim() || isSubmitting}
                  className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
            )}
            {selectedTicket.status === 'Çözüldü' && (
              <div className="mt-auto bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-4 rounded-xl text-center text-sm border border-green-200 dark:border-green-800/50">
                Bu bilet çözülmüş olarak işaretlendiği için yeni mesaj gönderilemez.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
